import apiService from '../../../services/api'
import { openExternalPaymentLink } from '../utils/openExternalPaymentLink'

/**
 * СБП (Система Быстрых Платежей) Payment API Service
 * Сервис для работы с оплатой через СБП (только для РФ)
 */

/**
 * Получить конфигурацию СБП (курсы валют и лимиты)
 * @returns {Promise<Object>} Конфигурация с курсами и лимитами
 * @returns {number} min_rub - Минимальная сумма в рублях
 * @returns {number} max_rub - Максимальная сумма в рублях
 * @returns {number} price_one_crystal_in_rub - Стоимость 1 кристалла в рублях
 * @returns {number} price_one_rub_in_crystal - Стоимость 1 рубля в кристаллах
 */
export const fetchSbpConfig = async () => {
  try {
    const response = await apiService.makeRequest('/api/sbp1/')
    
    if (response.sbp1) {
      return { ...response.sbp1, promo: response.promo || null }
    } else {
      throw new Error('Invalid response format: sbp1 config not found')
    }
  } catch (error) {
    console.error('Error fetching SBP config:', error)
    throw error
  }
}

/**
 * Получить доступные пакеты для СБП из общего списка тарифов
 * Фильтрует только те пакеты, у которых есть поле sbp1
 * @returns {Promise<Array>} Массив пакетов с ценами в рублях
 */
export const fetchSbpPackages = async () => {
  try {
    const response = await apiService.makeRequest('/api/topup/')
    
    if (response.topup && Array.isArray(response.topup)) {
      // Фильтруем только пакеты с полем sbp1
      const sbpPackages = response.topup.filter(pkg => pkg.sbp1 !== undefined)
      return sbpPackages
    } else {
      throw new Error('Invalid response format: topup array not found')
    }
  } catch (error) {
    console.error('Error fetching SBP packages:', error)
    throw error
  }
}

/**
 * Создать платеж СБП по количеству кристаллов
 * @param {number} crystals - Количество кристаллов для покупки
 * @returns {Promise<{link: string}>} Ссылка на оплату
 */
export const createPaymentByCrystals = async (crystals) => {
  try {
    const response = await apiService.makeRequest('/api/sbp1/', {
      method: 'POST',
      body: JSON.stringify({ crystal: crystals })
    })

    if (response.error) {
      // Обработка специфичных ошибок
      if (response.error === 'error sum') {
        throw new Error('INVALID_AMOUNT')
      } else if (response.error === 'error backend') {
        throw new Error('BACKEND_ERROR')
      } else {
        throw new Error(response.error)
      }
    }

    if (response.link) {
      return { link: response.link }
    } else {
      throw new Error('Invalid response format: link not found')
    }
  } catch (error) {
    console.error('Error creating SBP payment by crystals:', error)
    throw error
  }
}

/**
 * Создать платеж СБП по сумме в рублях
 * @param {number} rubles - Сумма в рублях
 * @returns {Promise<{link: string}>} Ссылка на оплату
 */
export const createPaymentByRubles = async (rubles) => {
  try {
    const response = await apiService.makeRequest('/api/sbp1/', {
      method: 'POST',
      body: JSON.stringify({ rub: rubles })
    })

    if (response.error) {
      // Обработка специфичных ошибок
      if (response.error === 'error sum') {
        throw new Error('INVALID_AMOUNT')
      } else if (response.error === 'error backend') {
        throw new Error('BACKEND_ERROR')
      } else {
        throw new Error(response.error)
      }
    }

    if (response.link) {
      return { link: response.link }
    } else {
      throw new Error('Invalid response format: link not found')
    }
  } catch (error) {
    console.error('Error creating SBP payment by rubles:', error)
    throw error
  }
}

/**
 * Открыть ссылку на оплату СБП
 * @param {string} paymentLink - Ссылка на оплату
 * @param {Function} onRedirect - Callback после редиректа
 * @param {Window} paymentWindow - Окно для оплаты
 */
export const openSbpPayment = (paymentLink, onRedirect, paymentWindow = null) => {
  openExternalPaymentLink(paymentLink, onRedirect, 'SBP', paymentWindow)
}

// Экспорт как объект для удобства
export default {
  fetchSbpConfig,
  fetchSbpPackages,
  createPaymentByCrystals,
  createPaymentByRubles,
  openSbpPayment
}
